import React, { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { IProduct } from '../../../../utils/types/contentful';
import { ProductPackDetails } from './Details/productPackDetails';
import { formatContentfulComponentProps } from '../../../../utils/globals';
import { ProductMainPackImages } from './PackImages/productMainPackImages';
import {
  productPackState,
  productPackStepOptionState,
} from '../../../../utils/recoil/product';
import { pushProductVariantDetailsToDataLayer } from '../../../../utils/dataLayer/productDetail';
import { useMediaQuery } from '../../../../utils/hooks/useMediaQuery';
import { ProductPackOptionsSelector } from './Pack/OptionSelector/productPackOptionsSelector';
import { ProductPackMainRecommendations } from './Details/Recommendations/productPackMainRecommendations';
import { ProductMainVariantDetailsInfos } from './Details/Infos/productMainDetailsInfos';

export interface ProductMainProps {
  product: IProduct;
}

export const ProductPackMain: React.FC<ProductMainProps> = ({
  product,
  productMainDetails,
  productPackPicker,
}) => {
  const {
    option1,
    option1Products,
    option2,
    option2Products,
    option3,
    option3Products,
  } = product;

  const setProductPack = useSetRecoilState(productPackState);
  const setProductPackStepOptionState = useSetRecoilState(
    productPackStepOptionState
  );

  const isWindowSmallSize = useMediaQuery('lg');

  // create iterable list from product pack options
  const productPackOptionList = [
    option1 &&
      option1Products && { component: option1, products: option1Products },
    option2 &&
      option2Products && { component: option2, products: option2Products },
    option3 &&
      option3Products && { component: option3, products: option3Products },
  ].filter((option) => !!option);

  useEffect(() => {
    // Push product variant details to data layer
    if (product.fields?.shopifyData?.variants?.length > 0) {
      pushProductVariantDetailsToDataLayer(
        product.fields.shopifyData?.variants[0]
      );
    }
    // Initialise product pack state from the list of options
    setProductPack(productPackOptionList.map(() => null));
    // Initialise product pack step state from the list of options to 1
    setProductPackStepOptionState(productPackOptionList.map(() => 1));
  }, []);

  return (
    // The responsibility of displaying the components is shared between ProductPackMain and ProductPackDetails
    <div className='bg-white flex justify-center pb-20 items-center w-screen'>
      <div className='relative w-full main-wrapper flex flex-wrap'>
        <div className='w-full lg:w-2/3 lg:pr-6 pt-12'>
          <ProductMainPackImages
            categories={product.categories}
            images={product.images}
          />
          {/* Display ProductPackDetails for responsive */}
          {isWindowSmallSize && (
            <ProductPackDetails
              {...formatContentfulComponentProps(productMainDetails)}
              product={product}
              productPackOptionList={productPackOptionList}
              productPackPicker={productPackPicker}
            />
          )}
          <ProductPackOptionsSelector
            productPackOptionList={productPackOptionList}
            productPackPicker={productPackPicker}
          />
        </div>
        <div className='relative w-full lg:pl-2 lg:w-1/3'>
          {/* Display ProductPackDetails for desktop */}
          {!isWindowSmallSize && (
            <ProductPackDetails
              {...formatContentfulComponentProps(productMainDetails)}
              product={product}
              productPackOptionList={productPackOptionList}
              productPackPicker={productPackPicker}
            />
          )}
          {/* Display ProductPackMainRecommendations for responsive (desktop in ProductPackDetails) */}
          {product.recommendations && isWindowSmallSize && (
            <ProductPackMainRecommendations
              {...formatContentfulComponentProps(productMainDetails)
                .recommendation}
              recommendations={product.recommendations}
            />
          )}
          {/* Display ProductMainVariantDetailsInfos for responsive (desktop in ProductPackDetails) */}
          {isWindowSmallSize && (
            <ProductMainVariantDetailsInfos
              informations={product.informations}
            />
          )}
        </div>
      </div>
    </div>
  );
};
